import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BookOpen, PieChart, LogOut, Star, Menu, GraduationCap } from './LordIcons';
import { cn } from './ui/utils';
import { Button } from './ui/button';
import { useAuth } from './ProtectedRoute';
import { useNotifications } from '../contexts/NotificationsContext';
import Logo from './Logo';
import { useState, useEffect } from 'react';
import useNaturalScrollReveal from '../hooks/useNaturalScrollReveal';

/**
 * @typedef {Object} LayoutProps
 * @property {React.ReactNode} children
 */

const navigation = [
	{
		name: 'Dashboard',
		href: '/Dashboard',
		icon: PieChart,
		allowedRoles: ['admin', 'bbs_staff', 'cana_staff', 'parochial_staff'],
	},
	{
		name: 'Announcements',
		href: '/Announcements',
		icon: Star,
		allowedRoles: ['admin'],
	},
	{
		name: 'Certificates',
		href: '/seminar',
		icon: BookOpen,
		allowedRoles: ['admin', 'bbs_staff', 'cana_staff', 'parochial_staff'],
	},
	{
		name: 'Basic Bible Seminar',
		href: '/BasicBibleSeminar',
		icon: GraduationCap,
		allowedRoles: ['admin', 'bbs_staff', 'parochial_staff'],
	},
];

const spiritualQuotes = [
	'"All Scripture is God-breathed..." - 2 Timothy 3:16',
	'"Faith comes by hearing..." - Romans 10:17',
	'"Iron sharpens iron..." - Proverbs 27:17',
	'"Where two or three gather..." - Matthew 18:20',
	'"Study to show thyself approved..." - 2 Timothy 2:15',
];

export default function Layout({ children }) {
	const location = useLocation();
	const navigate = useNavigate();
	const { user, logout } = useAuth();
	const { showSuccess } = useNotifications();
	const [mobileNavOpen, setMobileNavOpen] = useState(false);
	useNaturalScrollReveal();

	const dailyQuote = getCurrentQuote();

	const handleLogout = () => {
		logout();
		showSuccess('Go in peace, God bless you!');
		navigate('/login');
	};

	useEffect(() => {
		const handleKeydown = (event) => {
			if (event.key === 'Escape') {
				setMobileNavOpen(false);
			}
		};

		if (mobileNavOpen) {
			document.body.style.overflow = 'hidden';
			window.addEventListener('keydown', handleKeydown);
		} else {
			document.body.style.overflow = '';
		}

		return () => {
			document.body.style.overflow = '';
			window.removeEventListener('keydown', handleKeydown);
		};
	}, [mobileNavOpen]);

	return (
		<div className="flex min-h-screen flex-col bg-white lg:flex-row">
			{/* Skip to content for accessibility */}
			<a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:px-4 focus:py-2 focus:text-white" style={{ backgroundColor: '#2E7D32' }}>
				Skip to content
			</a>
			<div
				className={cn(
					'fixed inset-0 z-30 bg-slate-900/60 backdrop-blur-sm transition-opacity lg:hidden',
					mobileNavOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
				)}
				onClick={() => setMobileNavOpen(false)}
				aria-hidden={!mobileNavOpen}
			/>
			<aside
				id="primary-sidebar"
				role="navigation"
				aria-label="Primary"
				className={cn(
					'fixed inset-y-0 left-0 z-40 flex w-64 sm:w-72 md:w-80 flex-col bg-white border-r border-green-100 text-slate-800 transition-transform duration-200 lg:static lg:w-72 lg:translate-x-0 shadow-sm',
					mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
				)}
			>
				<div className="border-b border-green-100 p-4 sm:p-5 md:p-6 pb-3 sm:pb-4 md:pb-5">
					<Logo size="lg" className="mb-3 sm:mb-4 md:mb-5" />
					<div className="rounded-2xl border border-green-100 bg-green-50 p-3 sm:p-4 shadow-sm">
						<div className="mb-1 sm:mb-2 flex items-center gap-2">
							<Star size={14} colors="primary:#1f7a3d,secondary:#c9a54b" className="h-3 sm:h-4 w-3 sm:w-4 flex-shrink-0" />
							<span className="text-xs font-semibold uppercase tracking-wider text-slate-700">Daily Note</span>
						</div>
						<p className="text-xs sm:text-sm leading-relaxed text-slate-700 italic">{dailyQuote}</p>
					</div>
				</div>

				<nav className="flex-1 space-y-1 sm:space-y-2 px-3 sm:px-4 md:px-5 py-4 sm:py-6 md:py-8">
					{navigation.map((item, index) => {
						// Check if user has permission to see this nav item
						if (item.allowedRoles && user && !item.allowedRoles.includes(user.role)) {
							return null;
						}
						const isActive = item.href === '/'
							? location.pathname === '/'
							: location.pathname.startsWith(item.href);
						return (
							<div key={item.name} className="animate-in slide-in-from-left fade-in duration-500 fill-mode-forwards" style={{ animationDelay: `${index * 100}ms` }}>
								<Link
									to={item.href}
									className={cn(
										'group flex items-center gap-2 sm:gap-3 rounded-lg md:rounded-xl px-2 sm:px-3 md:px-4 py-2 sm:py-3 text-xs sm:text-sm font-semibold transition-all duration-300 ease-out min-h-10 fluent-hover active:scale-[0.98]',
										isActive
											? 'bg-green-800 shadow-lg text-white border border-green-700'
											: 'text-slate-800 hover:bg-green-50 hover:text-green-800 hover:shadow-sm border border-transparent'
									)}
									aria-current={isActive ? 'page' : undefined}
									onClick={() => setMobileNavOpen(false)}
								>
									<span
										className={cn(
											'flex h-8 sm:h-9 md:h-10 w-8 sm:w-9 md:w-10 items-center justify-center rounded-lg transition-colors flex-shrink-0',
											isActive
												? 'text-white'
												: 'text-slate-600 group-hover:text-green-800'
										)}
									>
										<item.icon
											size={18}
											colors={isActive ? 'primary:#ffffff,secondary:#f4de9b' : 'primary:#1f7a3d,secondary:#c9a54b'}
											className="h-4 sm:h-5 w-4 sm:w-5"
										/>
									</span>
									<span className="truncate tracking-tight hidden sm:inline">{item.name}</span>
								</Link>
							</div>
						);
					})}
				</nav>

				<div className="border-t border-green-100 p-4">
					<Button
						variant="ghost"
						onClick={handleLogout}
						className="w-full justify-start gap-2 rounded-xl text-slate-700 hover:bg-red-50 hover:text-red-600 transition-all"
					>
						<LogOut size={26} colors="primary:#1f7a3d,secondary:#c9a54b" className="h-8 w-8" />
						Sign out
					</Button>

					<p className="mt-4 text-center text-[10px] uppercase tracking-widest text-slate-500 font-medium">
						© Cana 101 System
					</p>
				</div>
			</aside>

			<main id="main-content" className="flex w-full flex-1 flex-col bg-transparent lg:overflow-y-auto">
				<header className="sticky top-0 z-10 flex items-center justify-between border-b border-green-100 bg-white px-3 sm:px-4 py-2 sm:py-3 lg:hidden shadow-sm">
					<button
						type="button"
						className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs sm:text-sm font-semibold text-white bg-green-800 shadow-sm active:scale-95 transition-transform h-10 min-w-[80px]"
						onClick={() => setMobileNavOpen(true)}
						aria-haspopup="true"
						aria-expanded={mobileNavOpen}
						aria-controls="primary-sidebar"
					>
						<Menu size={18} colors="primary:#ffffff,secondary:#f4de9b" className="h-4 w-4 sm:h-5 sm:w-5" />
						<span className="hidden xs:inline">Menu</span>
					</button>
					<Logo size="sm" showText={false} />
					<div className="flex items-center gap-2 sm:gap-3">
						{user && (
							<div className="flex h-9 sm:h-10 w-9 sm:w-10 items-center justify-center rounded-full bg-green-800 text-xs sm:text-sm font-semibold text-white shadow-md">
								{getUserInitials(user.name)}
							</div>
						)}
						<Button
							variant="ghost"
							size="sm"
							onClick={handleLogout}
							className="h-9 sm:h-10 w-9 sm:w-10 rounded-full p-0 text-slate-600 hover:bg-slate-200"
							aria-label="Sign out"
						>
							<LogOut size={16} colors="primary:#1f7a3d,secondary:#c9a54b" className="h-4 w-4" />
						</Button>
					</div>
				</header>
				<div className="flex-1 p-3 sm:p-4 md:p-6 lg:p-8">{children}</div>
			</main>
		</div>
	);
}

function getCurrentQuote(date) {
	const baseDate = date ?? new Date();
	const index = baseDate.getDate() % spiritualQuotes.length;
	return spiritualQuotes[index];
}

function getUserInitials(name) {
	if (!name) {
		return 'BB';
	}

	const initials = name
		.split(' ')
		.filter(Boolean)
		.map((part) => part[0]?.toUpperCase())
		.join('');

	return initials || 'BB';
}